import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { LANGUAGES, type LanguageCode } from './i18n';

/**
 * Hook that wires i18next to profile.preferred_language.
 *
 * - When the profile loads with a preferred language, flip i18n to match.
 * - `setLanguage` flips i18n immediately (optimistic) and persists the
 *   choice to the profile so it follows the user across devices.
 */
export function useLanguage(): {
  language: LanguageCode;
  setLanguage: (code: LanguageCode) => Promise<void>;
  languages: typeof LANGUAGES;
} {
  const { i18n } = useTranslation();
  const { profile, updateProfile } = useAuth();

  // Apply profile's language once it loads. Only fires when it *changes*,
  // so a locally-chosen language before login stays in place.
  useEffect(() => {
    const preferred = profile?.preferred_language;
    if (preferred && preferred !== i18n.language) {
      void i18n.changeLanguage(preferred);
    }
  }, [profile?.preferred_language, i18n]);

  const setLanguage = async (code: LanguageCode): Promise<void> => {
    await i18n.changeLanguage(code);
    if (profile && profile.preferred_language !== code) {
      await updateProfile({ preferred_language: code });
    }
  };

  return {
    language: normalizeLanguage(i18n.language),
    setLanguage,
    languages: LANGUAGES,
  };
}

function normalizeLanguage(lang: string | undefined): LanguageCode {
  const base = (lang ?? 'en').slice(0, 2).toLowerCase();
  return (LANGUAGES.find((l) => l.code === base)?.code ?? 'en') as LanguageCode;
}
